import { LoginData, RegisterData } from '@/models/login.model';
import {
  Notification,
  NotificationTimeouts,
} from '@/models/notification.model';
import { createStore } from 'vuex';
import authService from './services/auth.service';
import mediaService from './services/media.service';
import { categoriesStore } from '@/stores/categories.store';
import { parseCookie, setCookieValue } from '@/util/cookie.util';
import { User } from './models/user.model';
import { Drawer } from './models/drawer.model';
import { storiesStore } from '@/stores/stories.store';
import { contentStore } from '@/stores/content.store';
import { co2QuizStore } from '@/stores/co2-quiz.store';
import { UsersMutations, usersStore } from './stores/users.store';
import { useEvents, Event } from '@/services/events.composable';
import { Media } from './models/media.model';
import statusService from '@/services/status.service';
import { bptStore } from '@/stores/bpt.store';
import { feedbackStore } from './stores/feedback.store';
import { labelsStore } from './stores/labels.store';
import { dataTablesStore } from '@/stores/data-tables.store';
import { municipalityStore } from '@/stores/municipalities.store';
import { learningStore } from './stores/learning.store';
import { eventsStore } from './stores/events.store';
import { goalsStore } from './stores/goals.store';
import { locationsStore } from './stores/locations.store';
import usersService from './services/users.service';

const events = useEvents();

export enum Mutations {
  GetState = 'GetState',
  SetToken = 'SetToken',
  SetVersion = 'SetVersion',
  SetLanguage = 'SetLanguage',
  SetLoggedInUser = 'SetLoggedInUser',
  AddNotification = 'AddNotification',
  ClearNotification = 'ClearNotification',
  ShowDrawer = 'ShowDrawer',
  HideDrawer = 'HideDrawer',
  ShowSubDrawer = 'ShowSubDrawer',
  HideSubDrawer = 'HideSubDrawer',
  SetDirtyState = 'SetDirtyState',
}

export enum Actions {
  Login = 'Login',
  LoginTwoFactor = 'LoginTwoFactor',
  Register = 'Register',
  Logout = 'Logout',
  VerifyToken = 'VerifyToken',
  GetCurrentUser = 'GetCurrentUser',
  GetVersion = 'GetVersion',
  ShowNotification = 'ShowNotification',
  UploadMedia = 'UploadMedia',
  SearchMedia = 'SearchMedia',
  AddMedia = 'AddMedia',
  UpdateMedia = 'UpdateMedia',
  SetLanguage = 'SetLanguage',
}

export interface ShiftCMSState {
  version: string;
  token: string;
  language: string;
  loggedInUser: User | null;
  notifications: Notification[];
  drawer: Drawer;
  subDrawer: Drawer;
  media: Media[];
  dirtyState: boolean;
}

export default createStore<ShiftCMSState>({
  state: {
    version: '',
    token: '',
    language: 'nl',
    loggedInUser: null,
    notifications: [],
    drawer: { show: false },
    subDrawer: { show: false },
    media: [],
    dirtyState: false,
  },
  modules: {
    go: goalsStore,
    ev: eventsStore,
    les: learningStore,
    los: locationsStore,
    cs: categoriesStore,
    fs: feedbackStore,
    ts: storiesStore,
    us: usersStore,
    cns: contentStore,
    co2: co2QuizStore,
    bpt: bptStore,
    ls: labelsStore,
    ds: dataTablesStore,
    ms: municipalityStore,
  },
  mutations: {
    [Mutations.GetState]: (state: ShiftCMSState) => {
      const cookies = parseCookie(document.cookie);

      const token = cookies.accessToken || '';
      const lang = cookies.lang || 'en';

      state.token = token;

      state.language = lang;
    },
    [Mutations.SetToken]: (state: ShiftCMSState, token: string) => {
      state.token = token;
      // set a cookie to provide for reloads
      if (token) {
        setCookieValue('accessToken', `Bearer ${token}`);
      }
    },
    [Mutations.SetVersion]: (state: ShiftCMSState, version: string) => {
      state.version = version;
    },
    [Mutations.SetLanguage]: (state: ShiftCMSState, language: string) => {
      state.language = language;
    },
    [Mutations.SetDirtyState]: (state: ShiftCMSState, isDirty: boolean) => {
      state.dirtyState = isDirty;
    },
    [Mutations.SetLoggedInUser]: (state: ShiftCMSState, u: User | null) => {
      state.loggedInUser = u;
    },
    [Mutations.AddNotification]: (state: ShiftCMSState, n: Notification) => {
      state.notifications = [...state.notifications, n];
    },
    [Mutations.ClearNotification]: (state: ShiftCMSState, id: number) => {
      state.notifications = state.notifications.filter((n) => n.id !== id);
    },
    [Mutations.ShowDrawer]: (state: ShiftCMSState, drawer: Partial<Drawer>) => {
      state.drawer = {
        show: true,
        component: drawer.component,
        data: drawer.data,
        small: drawer.small,
        medium: drawer.medium,
      };
    },
    [Mutations.HideDrawer]: (state: ShiftCMSState) => {
      state.drawer = { show: false };

      events.emit(Event.DrawerClosed, null);
    },
    [Mutations.ShowSubDrawer]: (
      state: ShiftCMSState,
      drawer: Partial<Drawer>,
    ) => {
      state.subDrawer = {
        show: true,
        component: drawer.component,
        data: drawer.data,
        small: drawer.small,
      };
    },
    [Mutations.HideSubDrawer]: (state: ShiftCMSState) => {
      state.subDrawer = { show: false };
    },
  },
  actions: {
    async [Actions.GetCurrentUser]({ state, commit }) {
      commit(Mutations.GetState);

      if (state.token) {
        const { status, data } = await authService.getCurrentUser();

        if (status) {
          commit(Mutations.SetLoggedInUser, data);
        }
        return;
      }
      commit(Mutations.SetLoggedInUser, null);
    },
    async [Actions.Login]({ commit, dispatch }, loginData: LoginData) {
      const { status, data } = await authService.login(loginData);

      if (status && data && !data.twoFactorEnabled) {
        commit(Mutations.SetToken, data?.accessToken);

        dispatch(Actions.GetCurrentUser);

        return {
          loggedIn: data?.twoFactorRequired ? false : true,
          twoFactorRequired: data?.twoFactorRequired,
        };
      } else if (!data?.twoFactorEnabled) {
        dispatch(Actions.ShowNotification, {
          message: 'Login failed',
          type: 'error',
        });
      }
      return {
        loggedIn: false,
        twoFactorEnabled: data?.twoFactorEnabled,
      };
    },
    async [Actions.LoginTwoFactor](
      { commit, dispatch },
      loginData: LoginData & { token: string },
    ) {
      const data = await usersService.validate2FA(loginData);

      if (data && data.accessToken) {
        commit(Mutations.SetToken, data?.accessToken);

        dispatch(Actions.GetCurrentUser);

        return { loggedIn: true };
      }
    },
    async [Actions.GetVersion]({ commit }) {
      const { status, data } = await statusService.getVersion();

      if (status) {
        commit(Mutations.SetVersion, data.version);
      }
    },
    async [Actions.Register]({ dispatch }, registerData: RegisterData) {
      const { status } = await authService.register(registerData);

      if (status) {
        return true;
      } else {
        dispatch(Actions.ShowNotification, {
          message: 'Registration failed',
          type: 'error',
        });
      }
      return false;
    },
    async [Actions.Logout]({ commit }) {
      const { status } = await authService.logout();

      if (status) {
        commit(Mutations.SetToken, '');
        commit(Mutations.SetLoggedInUser, null);
        commit(UsersMutations.SetUser, null);

        return true;
      }
      return false;
    },
    async [Actions.SetLanguage]({ commit }, language: string) {
      await setCookieValue('lang', `${language}`);

      commit(Mutations.SetLanguage, language);
    },
    [Actions.ShowNotification]({ commit }, message: Notification) {
      const id = Date.now();
      commit(Mutations.AddNotification, { id, ...message });

      setTimeout(() => {
        commit(Mutations.ClearNotification, id);
      }, NotificationTimeouts[message.type]);
    },
    async [Actions.UploadMedia](
      _,
      { uuid, media }: { uuid?: string; media: Media },
    ): Promise<Media | null> {
      const response = await mediaService.upload(media, uuid);

      return response.status ? response.data : null;
    },
    [Actions.SearchMedia](_, search: string) {
      return mediaService.search(search);
    },
    async [Actions.AddMedia](
      _,
      { uuid, media }: { uuid?: string; media: Media },
    ): Promise<Media | null> {
      const response = await mediaService.add(media, uuid);
      return response.status ? response.data : null;
    },
    async [Actions.UpdateMedia](
      _,
      { uuid, media }: { uuid?: string; media: Media },
    ): Promise<Media | null> {
      const response = await mediaService.edit(media, uuid);
      return response.status ? response.data : null;
    },
  },
  getters: {
    showNotification: (state: ShiftCMSState): boolean => {
      return state.notifications.length !== 0 ? true : false;
    },
    language: (state: ShiftCMSState): string => {
      return state.language === 'nl' ? 'Dutch' : 'English';
    },
  },
});
